import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  model,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { SharedModule } from '@shared/shared.module';
import { UsersHttpService } from '@services/http/users/users-http.service';
import { User } from '@interfaces/a-organizar/user';
import { MonitoringPicklistMember } from './monitoring-user-picklist.model';

@Component({
  selector: 'app-monitoring-user-picklist',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule],
  templateUrl: './monitoring-user-picklist.component.html',
  styleUrl: './monitoring-user-picklist.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MonitoringUserPicklistComponent {
  readonly members = model<MonitoringPicklistMember[]>([]);
  readonly showCommitteeMemberFields = input(false);
  readonly minMembers = input(2);
  readonly companyId = input<string | null>(null);
  /** Incrementado pelo pai em `reset()` para limpar o campo de busca. */
  readonly resetVersion = input(0);
  /** E-mails pré-selecionados uma vez por `resetVersion` após carregar usuários. */
  readonly defaultMemberEmails = input<string[]>([]);
  readonly addPanelTitle = input('Adicionar Membros');
  readonly selectedPanelTitle = input('Composição do Colegiado');

  private readonly usersHttpService = inject(UsersHttpService);
  private readonly router = inject(Router);
  private readonly doc = inject(DOCUMENT);
  private readonly cdr = inject(ChangeDetectorRef);

  userSearch = '';
  availableUsers: User[] = [];
  isLoadingUsers = false;

  private resetSeen = -1;
  private defaultsAppliedForResetVersion = -1;

  constructor() {
    effect(() => {
      const v = this.resetVersion();
      if (this.resetSeen >= 0 && v !== this.resetSeen) {
        this.userSearch = '';
        this.loadUsers();
        this.cdr.markForCheck();
      }
      this.resetSeen = v;
    });

    effect(() => {
      this.companyId();
      this.loadUsers();
    });
  }

  get filteredUsers(): User[] {
    const search = this.userSearch.toLowerCase().trim();
    if (!search) return this.availableUsers;
    return this.availableUsers.filter(
      u =>
        `${u.firstName} ${u.surName}`.toLowerCase().includes(search) ||
        u.email.toLowerCase().includes(search)
    );
  }

  isMemberAdded(userId: string): boolean {
    return this.members().some(m => m.userId === userId);
  }

  addMember(user: User): void {
    if (this.isMemberAdded(user.id)) return;
    const display = `${user.firstName} ${user.surName}`.trim();
    const next: MonitoringPicklistMember[] = [
      ...this.members(),
      {
        memberType: 'User',
        userId: user.id,
        name: display,
        displayName: display,
        email: user.email,
        roleLabel: '',
        hasVotingPower: true,
      },
    ];
    this.members.set(next);
    this.cdr.markForCheck();
  }

  removeMember(userId: string): void {
    this.members.set(this.members().filter(m => m.userId !== userId));
    this.cdr.markForCheck();
  }

  toggleVotingPower(member: MonitoringPicklistMember): void {
    const next = this.members().map(m =>
      m.userId === member.userId ? { ...m, hasVotingPower: !m.hasVotingPower } : m
    );
    this.members.set(next);
    this.cdr.markForCheck();
  }

  updateRoleLabel(member: MonitoringPicklistMember, value: string): void {
    const next = this.members().map(m =>
      m.userId === member.userId ? { ...m, roleLabel: value } : m
    );
    this.members.set(next);
  }

  forceRefresh(): void {
    this.loadUsers();
  }

  /** Abre cadastro de usuário em nova aba (`/dashboard/user/register[/company/:id]`). */
  openUserRegisterInNewTab(): void {
    const win = this.doc.defaultView;
    if (!win) return;

    const cid = this.companyId()?.trim();
    const tree =
      cid && cid.length > 0
        ? this.router.createUrlTree(['/dashboard', 'user', 'register', 'company', cid])
        : this.router.createUrlTree(['/dashboard', 'user', 'register']);

    const path = this.router.serializeUrl(tree);
    const href = path.startsWith('/') ? new URL(path, win.location.href).href : path;
    win.open(href, '_blank', 'noopener,noreferrer');
  }

  private applyDefaultMembersIfNeeded(): void {
    const resetVersion = this.resetVersion();
    if (this.defaultsAppliedForResetVersion === resetVersion) return;

    const emails = this.defaultMemberEmails()
      .map(email => email.toLowerCase().trim())
      .filter(Boolean);
    if (emails.length === 0) {
      this.defaultsAppliedForResetVersion = resetVersion;
      return;
    }

    for (const email of emails) {
      const user = this.availableUsers.find(
        candidate => candidate.email.toLowerCase().trim() === email
      );
      if (user) {
        this.addMember(user);
      }
    }

    this.defaultsAppliedForResetVersion = resetVersion;
  }

  private loadUsers(): void {
    this.isLoadingUsers = true;
    this.cdr.markForCheck();
    this.usersHttpService.getAllCompanyAndBackofficeUsers(this.companyId(), true).subscribe({
      next: users => {
        this.availableUsers = users ?? [];
        this.isLoadingUsers = false;
        this.applyDefaultMembersIfNeeded();
        this.cdr.markForCheck();
      },
      error: () => {
        this.availableUsers = [];
        this.isLoadingUsers = false;
        this.cdr.markForCheck();
      },
    });
  }
}
